// src/lib/push.js
// ──────────────────────────────────────────────────────────────────────────
// Firebase Cloud Messaging on the browser: ask permission, get a device
// token, and store it against the signed-in user so the server can push to
// them later.
//
// FCM is only the transport — the app stays on Supabase. The Firebase project
// needs nothing beyond the free Spark plan, because we never deploy a Firebase
// Cloud Function (those require the paid Blaze plan as of February 2026).
//
// Everything here degrades quietly: no config, an unsupported browser, or a
// denied permission simply means no push, and the in-app notifications feed
// carries on working.
// ──────────────────────────────────────────────────────────────────────────

import { supabase, supabaseReady } from './supabase';

const cfg = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  messagingSenderId: process.env.REACT_APP_FIREBASE_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
};
const VAPID_KEY = process.env.REACT_APP_FIREBASE_VAPID_KEY;

export const pushConfigured = Boolean(cfg.apiKey && cfg.projectId && cfg.messagingSenderId && VAPID_KEY);

// Push needs a service worker, the Notification API, and (in every browser
// that matters) a secure context. localhost counts as secure.
export const pushSupported = () =>
  typeof window !== 'undefined' &&
  'serviceWorker' in navigator &&
  'Notification' in window &&
  (window.isSecureContext || window.location.hostname === 'localhost');

export const permissionState = () =>
  (typeof window !== 'undefined' && 'Notification' in window ? Notification.permission : 'unsupported');

let messagingPromise = null;

// firebase is a large dependency — only pull it in when push is actually used.
async function getMessaging() {
  if (!messagingPromise) {
    messagingPromise = (async () => {
      const [{ initializeApp, getApps }, msg] = await Promise.all([
        import('firebase/app'),
        import('firebase/messaging'),
      ]);
      if (!(await msg.isSupported())) throw new Error('This browser does not support push notifications.');
      const app = getApps().length ? getApps()[0] : initializeApp(cfg);
      return { messaging: msg.getMessaging(app), msg };
    })();
  }
  return messagingPromise;
}

// Register the service worker that shows notifications while the tab is shut.
// The config is handed over by query string so the worker needs no build step.
async function registerWorker() {
  const params = new URLSearchParams({
    apiKey: cfg.apiKey || '',
    authDomain: cfg.authDomain || '',
    projectId: cfg.projectId || '',
    messagingSenderId: cfg.messagingSenderId || '',
    appId: cfg.appId || '',
  });
  const registration = await navigator.serviceWorker.register(`/firebase-messaging-sw.js?${params.toString()}`);
  // register() resolves as soon as the worker is *registered*, which may be
  // while it's still installing. FCM's getToken() needs an ACTIVE worker, so
  // wait for activation or it fails with "no active Service Worker".
  await waitForActive(registration);
  return registration;
}

function waitForActive(registration) {
  if (registration.active) return Promise.resolve(registration);
  const worker = registration.installing || registration.waiting;
  if (!worker) return navigator.serviceWorker.ready.then(() => registration);

  return new Promise((resolve, reject) => {
    const timer = setTimeout(
      () => reject(new Error('The notification service worker took too long to start.')),
      15000,
    );
    const onChange = () => {
      if (worker.state === 'activated') {
        clearTimeout(timer); worker.removeEventListener('statechange', onChange); resolve(registration);
      } else if (worker.state === 'redundant') {
        clearTimeout(timer); worker.removeEventListener('statechange', onChange);
        reject(new Error('The notification service worker failed to install.'));
      }
    };
    worker.addEventListener('statechange', onChange);
    onChange(); // in case it activated between the check and the listener
  });
}

/**
 * Ask for permission and register this device.
 * Returns { ok, reason } — reason explains any refusal so the UI can say why.
 */
export async function enablePush() {
  if (!pushConfigured) return { ok: false, reason: 'not-configured' };
  if (!pushSupported()) return { ok: false, reason: 'unsupported' };
  if (!supabaseReady) return { ok: false, reason: 'no-backend' };

  const { data: u } = await supabase.auth.getUser();
  if (!u?.user) return { ok: false, reason: 'signed-out' };

  const permission = await Notification.requestPermission();
  if (permission !== 'granted') return { ok: false, reason: permission }; // 'denied' | 'default'

  // Anything below can throw (worker install, network, FCM). Report it as a
  // reason rather than letting it bubble up as an uncaught runtime error.
  try {
    const { messaging, msg } = await getMessaging();
    const registration = await registerWorker();
    const token = await msg.getToken(messaging, { vapidKey: VAPID_KEY, serviceWorkerRegistration: registration });
    if (!token) return { ok: false, reason: 'no-token' };

    // Tokens rotate; upsert on the token itself so re-registering is harmless.
    const { error } = await supabase.from('device_tokens').upsert({
      token,
      user_id: u.user.id,
      user_agent: navigator.userAgent.slice(0, 300),
      last_seen_at: new Date().toISOString(),
    }, { onConflict: 'token' });
    if (error) return { ok: false, reason: error.message };

    return { ok: true, token };
  } catch (e) {
    return { ok: false, reason: e?.message || String(e) };
  }
}

/** Stop pushes to this device and forget the token. */
export async function disablePush() {
  if (!pushConfigured || !pushSupported()) return { ok: true };
  try {
    const { messaging, msg } = await getMessaging();
    const token = await msg.getToken(messaging, { vapidKey: VAPID_KEY }).catch(() => null);
    if (token) {
      await supabase.from('device_tokens').delete().eq('token', token);
      await msg.deleteToken(messaging).catch(() => {});
    }
    return { ok: true };
  } catch (e) {
    return { ok: false, reason: e.message };
  }
}

/**
 * Show notifications that arrive while the portal tab is in the foreground —
 * the service worker deliberately doesn't fire in that case.
 * Returns an unsubscribe function.
 */
export async function onForegroundMessage(handler) {
  if (!pushConfigured || !pushSupported() || permissionState() !== 'granted') return () => {};
  try {
    const { messaging, msg } = await getMessaging();
    return msg.onMessage(messaging, (payload) => {
      handler({
        title: payload?.notification?.title || payload?.data?.title || 'Notification',
        body: payload?.notification?.body || payload?.data?.body || '',
        url: payload?.data?.url || '',
      });
    });
  } catch {
    return () => {};
  }
}

// ── In-app feed (works with or without push permission) ────────────────────
export async function fetchNotifications(limit = 30) {
  if (!supabaseReady) return [];
  const { data: u } = await supabase.auth.getUser();
  if (!u?.user) return [];
  const { data, error } = await supabase.from('notifications')
    .select('*').eq('user_id', u.user.id).order('created_at', { ascending: false }).limit(limit);
  return error ? [] : data;
}

export async function markNotificationRead(id) {
  const { error } = await supabase.from('notifications')
    .update({ read_at: new Date().toISOString() }).eq('id', id);
  if (error) throw error;
}

export async function markAllNotificationsRead() {
  const { data: u } = await supabase.auth.getUser();
  if (!u?.user) return;
  await supabase.from('notifications')
    .update({ read_at: new Date().toISOString() })
    .eq('user_id', u.user.id).is('read_at', null);
}
